import axios from 'axios';
import * as Yup from 'yup';
import {Formik} from 'formik';
import {Button} from '@material-ui/core';
import {FormattedMessage, useIntl} from 'react-intl';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory, useLocation, useParams} from 'react-router';
import React, {useCallback, useEffect, useState, useMemo} from 'react';

import Util from '../../../util';
import URL from '../../../config/url';
import {RootState} from '../../../store';
import {getProjectData} from '../../../store/project';
import {Project} from '../../../types/project/Project';
import {toast} from '../../../base-components/StudioToast';
import {useTestListByLatest} from '../../../api/test/TestList';
import {StatusResponse} from '../../../types/operation/StatusResponse';
import {ApplicationInfo} from '../../../types/deployment/ApplicationsResponse';
import CreateDeploymentForm, {ProdDeployFormValues} from './CreateDeploymentForm';
import {DeploymentMapResponse} from '../../../types/deployment/DeploymentResponse';
import {AnalyticsParametersPrompts} from '../../../types/prompts/AnalyticsParameters';
import {DeploymentInitiateRequest} from '../../../types/deployment/DeploymentInitiateRequest';
import {fetchExternalStreamingDevices} from '../../../store/prodDeployments/create-deployment';
import {useDeploymentAnalyticParameters} from '../../../api/deployment/DeploymentAnalyticsParameters';
import {useDeploymentEnvironmentParameters} from '../../../api/deployment/DeploymentEnvironmentParameters';
import {DeviceGroupInfo} from '../../../types/deployment/DeploymentDeviceGroup';
import {DeploymentStatus} from '../../../types/operation/Status';
import {InputSource} from '../../../types/deployment/InferenceInputSource';
import {InputChoice} from '../DeploymentDetails/DeploymentDetails';

import './CreateDeployment.scss';

const DEPLOY_TASK_TYPE = 'DEPLOY_APPLICATION';

async function getNodes(applicationId: string) {
  const {data} = await axios.get(URL.COMPATIBLE_DEVICE_GROUP, {
    params: {applicationId: applicationId, onlineOnly: true, nodesType: 'SINGLE'},
  });
  return data.body;
}

async function getCompatibleGroupDevices(applicationId: string) {
  const {data} = await axios.get(URL.COMPATIBLE_DEVICE_GROUP, {
    params: {applicationId: applicationId, onlineOnly: true, nodesType: 'USER'},
  });
  return data.body;
}

export type StartUponDeploymentNodeType = {
  nodeId: string;
  inputSourceType: InputChoice | null;
  inputSource: string;
  externalSourceType: InputSource | null;
  deploymentId: string;
  deployStatus: string;
};

export const CreateDeployment = () => {
  const intl = useIntl();
  const history = useHistory();
  const dispatch = useDispatch();
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  const origin = params.get('origin');
  const deployId = params.get('deployId');

  // application Id here is from path /project/${projectId}/inference/${applicationId}/deployments/new
  const {projectId: initialProjectId, applicationId: inferenceApplicationId} = useParams<{
    projectId: string;
    applicationId: string;
  }>();
  // application Id here is from query params
  const paramsApplicationId = params.get('applicationId');

  // checking for application ID using the 2 above
  const applicationId = useMemo(
    () => (inferenceApplicationId ? inferenceApplicationId : paramsApplicationId),
    [inferenceApplicationId, paramsApplicationId]
  );

  const isProjectContext = useMemo(() => Boolean(initialProjectId), [initialProjectId]);

  const projectData: Project = useSelector(getProjectData);

  let [selectedApplication, setSelectedApplication] = useState<
    ApplicationInfo | undefined | null
  >(null);

  const [isDeploying, setIsDeploying] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');

  const [startUponDeploymentNodes, setStartUponDeploymentNodes] = useState<
    StartUponDeploymentNodeType[]
  >([]);

  const applications: Array<ApplicationInfo> = useSelector(
    (state: RootState) => state.createDeployment.applications
  );

  const updateStartUponDeployNodes = useCallback(
    (deploymentStatuses: DeploymentStatus[] = []) => {
      if (
        deploymentStatuses?.length > 0 &&
        startUponDeploymentNodes &&
        startUponDeploymentNodes?.length > 0
      ) {
        const nodes = startUponDeploymentNodes.map(node => {
          const nodeStatus = deploymentStatuses?.find(
            d => d.targetDeviceId === node.nodeId
          );
          if (nodeStatus) {
            return {
              ...node,
              deploymentId: nodeStatus.deploymentId,
              deployStatus: nodeStatus.status,
            };
          } else {
            return node;
          }
        });
        setStartUponDeploymentNodes(nodes);
      }
    },
    [startUponDeploymentNodes]
  );

  const monitorProgress = useCallback(
    (projectId: string) => {
      return new Promise<void | StartUponDeploymentNodeType[]>(resolve => {
        let isPolling = true;

        const pollForStatus = async () => {
          try {
            const {data} = await axios.get<StatusResponse>(
              URL.PROJECT_OP_STATUS(projectId)
            );
            const tasks = data.body?.tasks;
            const status = tasks?.find(task => task.type === DEPLOY_TASK_TYPE);
            if (status) {
              status.statusMessage && setStatusMessage(status.statusMessage);
              setTimeout(() => {
                updateStartUponDeployNodes(status.deploymentStatuses);
              }, 100);
              // get deployment status for nodes
              if (Util.isRealNumberEqual(status.percentCompleted, 100)) {
                isPolling = false;
                resolve();
              }
            }
          } finally {
            if (isPolling) {
              setTimeout(pollForStatus, 2000);
            }
          }
        };

        pollForStatus();
      });
    },
    [updateStartUponDeployNodes]
  );

  const finalizeDeployment = useCallback(
    async (
      projectId: string,
      deviceGroupId?: string,
      _applicationId?: string,
      autoDeployNodes?: StartUponDeploymentNodeType[]
    ) => {
      const {data} = await axios.post<DeploymentMapResponse>(
        URL.FINALIZE_DEPLOYMENT,
        null,
        {
          params: {projectId},
        }
      );
      if (data.errors?.length) {
        throw new Error('Finalize error');
      }

      const applicationId = _applicationId || selectedApplication?.id;

      if (autoDeployNodes && autoDeployNodes.length > 0) {
        const nodes = autoDeployNodes
          .filter(n => n.deploymentId && n.inputSourceType && n.inputSource)
          .map(n => {
            const inputSourceType =
              n.inputSourceType === 'DEVICE'
                ? 'DEVICE_CAMERA'
                : n.externalSourceType || null;
            return {
              inputSourceType: inputSourceType,
              inputSourceId: n.inputSource,
              deploymentId: n.deploymentId,
            };
          });
        await axios.post(URL.START_GROUP_DEPLOYMENT, {
          deviceGroupId,
          inferenceRequests: nodes,
        });

        if (nodes.length > 1 || (!deviceGroupId && !applicationId)) {
          return history.push('/deployments');
        }
      }

      const deploymentUrl = data.body[0].deploymentId
        ? `/deployment/${data.body[0].deploymentId}/details`
        : '/deployments';

      history.push(
        isProjectContext
          ? `${deploymentUrl}?projectContext=${initialProjectId}`
          : deploymentUrl
      );
    },
    [isProjectContext, initialProjectId, history, selectedApplication?.id]
  );

  useEffect(() => {
    const resumeDeployment = async () => {
      if (initialProjectId) {
        try {
          const {data} = await axios.get<StatusResponse>(
            URL.PROJECT_OP_STATUS(initialProjectId)
          );
          const {tasks} = data.body;
          const exists = tasks?.find(task => task.type === DEPLOY_TASK_TYPE);
          if (!exists) {
            return;
          }
          setIsDeploying(true);
          await monitorProgress(initialProjectId);
          await finalizeDeployment(
            initialProjectId,
            exists.deploymentStatuses?.[0]?.targetDeviceId,
            exists.applicationId
          );
        } catch (e) {
          console.error(e);
        } finally {
          setIsDeploying(false);
          setStatusMessage('');
        }
      }
    };
    resumeDeployment();
  }, [initialProjectId]);

  const isAnalyticApplication = useMemo(() => {
    let itIs = false;
    const projectApplications = projectData?.applications;

    if (selectedApplication && projectApplications) {
      itIs = Boolean(projectApplications[selectedApplication.id]?.analyticsApplication);
    }

    return itIs;
  }, [selectedApplication, projectData]);

  const {data: historyItems} = useTestListByLatest({
    avoidFetch: !deployId,
    projectId: projectData.id,
  });
  const deployHistoryItem = useMemo(
    () => historyItems?.entries.find(deployItem => deployItem.id === deployId),
    [historyItems, deployId]
  );

  const {
    data: originalAnalyticParameters,
    error: errorFetchingAnalyticParameters,
  } = useDeploymentAnalyticParameters({
    latest: !deployId,
    projectId: projectData.id,
    avoidFetch: !isAnalyticApplication,
    applicationId: selectedApplication?.id,
  });

  const analyticParametersPrompts = useMemo(() => {
    let prompts: AnalyticsParametersPrompts = originalAnalyticParameters || [];
    const historicItemAnalyticsParameters = deployHistoryItem?.analyticsParameters;

    if (originalAnalyticParameters && historicItemAnalyticsParameters) {
      prompts = prompts.map(({key, prefilledValue, ...rest}) => ({
        key,
        ...rest,
        prefilledValue: historicItemAnalyticsParameters[key],
      }));
    }

    prompts = prompts.map(prompt => ({
      ...prompt,
      disabled: isDeploying || prompt.disabled,
    }));

    return prompts;
  }, [isDeploying, originalAnalyticParameters, deployHistoryItem]);

  const {
    data: originalEnvironment,
    error: errorFetchingEnvironmentParameters,
  } = useDeploymentEnvironmentParameters({
    latest: !deployId, // If no specific item from history, get latest variables. If there's a specific item, use default variables and deploy variables.
    projectId: projectData.id,
    avoidFetch: !isAnalyticApplication,
    applicationId: selectedApplication?.id,
  });

  const [compatibleNodes, setCompatibleNodes] = useState<
    Array<DeviceGroupInfo> | undefined | null
  >(null);
  const [compatibleDeviceGroups, setCompatibleDeviceGroups] = useState<
    Array<DeviceGroupInfo> | undefined | null
  >(null);

  const selectApplication = async (app: ApplicationInfo | undefined | null) => {
    setSelectedApplication(app);
    if (!app) {
      setCompatibleNodes([]);
      setCompatibleDeviceGroups([]);
    } else {
      const nodes = await getNodes(app.id);
      setCompatibleNodes(nodes);
      const deviceGroups = await getCompatibleGroupDevices(app.id);
      setCompatibleDeviceGroups(deviceGroups);
    }
    setStartUponDeploymentNodes([]);
  };

  const newEnvironment = useMemo(
    () => Object.assign({}, originalEnvironment, deployHistoryItem?.environment || {}),
    [originalEnvironment, deployHistoryItem?.environment]
  );
  const [environment, setEnvironment] = useState(newEnvironment);

  useEffect(() => {
    if (JSON.stringify(newEnvironment) !== JSON.stringify(environment)) {
      setEnvironment(newEnvironment);
    }
  }, [newEnvironment, environment]);

  const initialValues: ProdDeployFormValues = useMemo(
    () => ({
      nodeId: '',
      deviceGroupId: '',
      environment: {},
      description: '',
      confThreshold: 0.25,
      ddaDataMaxSize: 1000,
      samplingFrequency: 60,
      analyticsParameters: [],
      nonMaximumSuppression: 0.5,
      projectId: initialProjectId || '',
      applicationId: applicationId || '',
      name: origin === 'test' ? selectedApplication?.name! : '',
    }),
    [origin, selectedApplication?.name, applicationId, initialProjectId]
  );

  const handleSubmit = async (values: ProdDeployFormValues) => {
    const {projectId} = values;
    const initiateRequestBody: DeploymentInitiateRequest = {
      projectId,
      name: values.name,
      nodeId: values.nodeId ? values.nodeId : null,
      deviceGroupId: values.deviceGroupId ? values.deviceGroupId : null,
      description: values.description,
      environment: values.environment,
      confThreshold: values.confThreshold,
      applicationId: values.applicationId,
      ddaDataMaxSize: values.ddaDataMaxSize,
      samplingFrequency: values.samplingFrequency,
      analyticsParameters: values.analyticsParameters,
      nonMaximumSuppression: values.nonMaximumSuppression,
    };

    try {
      setIsDeploying(true);
      const {data} = await axios.post<DeploymentMapResponse>(
        URL.INITIATE_DEPLOYMENT,
        initiateRequestBody
      );

      if (data.errors?.length) {
        throw new Error('Error initiating deployment');
      }

      let nodes: StartUponDeploymentNodeType[] = [];

      if (
        data.body.length &&
        (values.deviceGroupId || values.nodeId) &&
        startUponDeploymentNodes.length
      ) {
        nodes = startUponDeploymentNodes.map(node => {
          const deployment = data.body.find(d => d.targetDeviceId === node.nodeId);
          if (deployment) {
            return {
              ...node,
              deploymentId: deployment.deploymentId,
            };
          } else {
            return node;
          }
        });
        setStartUponDeploymentNodes(nodes);
      }

      await monitorProgress(projectId);
      await finalizeDeployment(
        projectId,
        values.deviceGroupId || values.nodeId,
        values.applicationId,
        nodes
      );
      setEnvironment(Object.assign({}, originalEnvironment));
    } catch (e) {
      console.error(e);
    } finally {
      setIsDeploying(false);
      setStatusMessage('');
    }
  };

  const prodDeployFormSchema = Yup.object().shape({
    name: Yup.string().required('Required'),
    description: Yup.string().nullable(true),
    projectId: Yup.string().required('Required'),
    applicationId: Yup.string().required('Required'),
    nodeId: Yup.string().when('deviceGroupId', {
      is: deviceGroupId => !deviceGroupId || deviceGroupId.length <= 0,
      then: Yup.string().required('Required'),
    }),
    deviceGroupId: Yup.string().nullable(true),
    samplingFrequency: Yup.number()
      .integer('Please enter an integer')
      .nullable(true)
      .required('Required'),
    nonMaximumSuppression: Yup.number()
      .min(0, 'Please enter a value between 0 and 1')
      .max(1, 'Please enter a value between 0 and 1')
      .nullable(true),
    ddaDataMaxSize: Yup.number()
      .integer('Please enter an integer')
      .nullable(true),
    confThreshold: Yup.number()
      .min(0, 'Please enter a value between 0 and 1')
      .max(1, 'Please enter a value between 0 and 1')
      .nullable(true),
  });

  useEffect(() => {
    dispatch(fetchExternalStreamingDevices());
    if (applicationId && selectedApplication == null) {
      const application: ApplicationInfo | undefined = applications.find(
        app => app.id === applicationId
      );
      selectApplication(application);
    }
  }, [dispatch, applications, applicationId, selectedApplication]);

  useEffect(() => {
    if (errorFetchingEnvironmentParameters) {
      toast.error(errorFetchingEnvironmentParameters);
    }
  }, [errorFetchingEnvironmentParameters]);

  useEffect(() => {
    if (errorFetchingEnvironmentParameters) {
      toast.error(
        intl.formatMessage({
          id: 'environmentParameters.errorGettingEnvironmentParameters',
        })
      );
    }
  }, [intl, errorFetchingEnvironmentParameters]);

  useEffect(() => {
    if (errorFetchingAnalyticParameters) {
      toast.error(
        intl.formatMessage({
          id: 'analyticsParameters.errorGettingAnalyticsParameters',
        })
      );
    }
  }, [intl, errorFetchingAnalyticParameters]);

  return (
    <div className="create-prod-deploy" data-testid="create-prod-deploy">
      <Formik
        initialValues={initialValues}
        validationSchema={prodDeployFormSchema}
        validateOnMount
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({dirty, isValid}) => (
          <>
            <div className="create-prod-deploy__menu">
              {isProjectContext && origin === 'test' && (
                <Button
                  className="create-prod-deploy__back"
                  variant="contained"
                  disableElevation
                  onClick={() => history.push(`/project/${initialProjectId}/test`)}
                >
                  <FormattedMessage id="prodDeployment.returnToTest" />
                </Button>
              )}
              <Button
                className="create-prod-deploy__back"
                variant="contained"
                disableElevation
                onClick={() =>
                  history.push(
                    isProjectContext
                      ? `/project/${initialProjectId}/deployments`
                      : '/deployments'
                  )
                }
              >
                <FormattedMessage id="createDeploy.viewAll" />
              </Button>
              <Button
                className="create-prod-deploy__submit"
                variant="contained"
                disableElevation
                color="primary"
                type="submit"
                form="prod-deploy-form"
                disabled={!isValid || isDeploying || !dirty}
              >
                <FormattedMessage id="form.submit" />
              </Button>
            </div>
            {isDeploying && (
              <p
                className="create-prod-deploy__status"
                data-testid="create-prod-deploy-status"
                id="create-prod-deploy-status"
              >
                {statusMessage || <FormattedMessage id={'prodDeployment.inProgress'} />}
              </p>
            )}
            <div className="create-prod-deploy__main">
              <h3>
                <FormattedMessage id="createDeploy.create" />
              </h3>
              <CreateDeploymentForm
                projectData={projectData}
                isDeploying={isDeploying}
                environmentParameters={environment}
                isProjectContext={isProjectContext}
                selectApplication={selectApplication}
                getCompatibleNodes={() => compatibleNodes}
                getCompatibleDeviceGroups={() => compatibleDeviceGroups}
                analyticParametersPrompts={analyticParametersPrompts}
                startUponDeploymentNodes={startUponDeploymentNodes}
                setStartUponDeploymentNodes={setStartUponDeploymentNodes}
              />
            </div>
          </>
        )}
      </Formik>
    </div>
  );
};
